import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import "../../styles/admin/admin-dashboard.css";
import "../../styles/admin/admin-form.css";
import config from "../../config";

const API_BASE_URL = String(config.apiBaseUrl || "").replace(/\/+$/, "");

function MainHeroCard({ activeUsers, todayRegistrations, monthRegistrations }) {
  const navigate = useNavigate();

  return (
    <section className="dashboard-hero-card">
      <div className="dashboard-hero-content">
        <span className="dashboard-hero-badge">Resumen general</span>

        <span className="dashboard-hero-label">Usuarios activos</span>
        <h2>{activeUsers}</h2>

        <button
          className="dashboard-hero-btn"
          type="button"
          onClick={() => navigate("/admin/usuarios")}
        >
          Ver datos completos
          <ArrowRight size={18} />
        </button>
      </div>

      <div className="dashboard-hero-visual">
        <div className="hero-circle hero-circle-one"></div>
        <div className="hero-circle hero-circle-two"></div>

        <div className="hero-card-mini">
          <span>Registros hoy</span>
          <strong>{todayRegistrations}</strong>
        </div>

        <div className="hero-card-mini second">
          <span>Registros este mes</span>
          <strong>{monthRegistrations}</strong>
        </div>
      </div>
    </section>
  );
}

function SideScoreCard({ total, dogs, cats }) {
  const dogPercent = total > 0 ? Math.round((dogs / total) * 100) : 0;
  const catPercent = total > 0 ? 100 - dogPercent : 0;

  return (
    <section className="dashboard-score-card">
      <div className="score-top">
        <span>Mascotas registradas</span>
        <strong>{total}</strong>
      </div>

      <div className="score-gauge">
        <div
          className="score-gauge-ring"
          style={{
            background: `conic-gradient(
              #cab19c 0deg ${dogPercent * 3.6}deg,
              #2e5e71 ${dogPercent * 3.6}deg 360deg
            )`,
          }}
        >
          <div className="score-gauge-inner"></div>
        </div>
      </div>

      <div className="score-legend">
        <div className="legend-item">
          <span className="legend-dot dog"></span>
          <span>Perros {dogPercent}%</span>
        </div>

        <div className="legend-item">
          <span className="legend-dot cat"></span>
          <span>Gatos {catPercent}%</span>
        </div>
      </div>
    </section>
  );
}

function MonthlyActivityCard({ data }) {
  const maxValue = Math.max(...data.map((item) => item.value), 1);

  return (
    <section className="dashboard-bottom-card monthly-card">
      <div className="monthly-card-header">
        <div>
          <h3>Actividad mensual</h3>
          <p>Usuarios registrados por mes</p>
        </div>
      </div>

      <div className="monthly-bars">
        {data.map((item) => (
          <div className="monthly-bar-item" key={item.month}>
            <div className="monthly-bar-track">
              <div
                className="monthly-bar-fill"
                style={{ height: `${(item.value / maxValue) * 100}%` }}
              ></div>
            </div>
            <span className="monthly-bar-label">{item.month}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

function AlliesSummaryCard({ totalAllies, recentAllies }) {
  const navigate = useNavigate();

  return (
    <section className="dashboard-bottom-card allies-summary-card">
      <div className="allies-summary-top">
        <div>
          <h3>Total aliados</h3>
          <strong className="allies-total-number">{totalAllies}</strong>
        </div>

        <button
          type="button"
          className="allies-total-btn"
          onClick={() => navigate("/admin/aliados")}
        >
          Ver total de aliados
        </button>
      </div>

      <div className="allies-list">
        {recentAllies.length > 0 ? (
          recentAllies.map((ally) => {
            const fullName = `${ally.name || ""} ${ally.lastName || ""}`.trim();

            return (
              <div className="ally-item" key={ally.idUser}>
                <div className="ally-avatar">{fullName.charAt(0) || "A"}</div>
                <div className="ally-text">
                  <strong>{fullName || "Aliado"}</strong>
                  <span>{ally.email || "Añadido recientemente"}</span>
                </div>
              </div>
            );
          })
        ) : (
          <div className="ally-item">
            <div className="ally-text">
              <strong>No hay aliados recientes</strong>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function DistributionMapCard() {
  return (
    <section className="dashboard-bottom-card map-card">
      <h3>Distribución</h3>

      <div className="distribution-map-card">
        <div className="world-map-svg"></div>

        <div className="map-point point-america"></div>
        <div className="map-point point-oceania"></div>

        <div className="map-highlight-card">
          <div className="map-highlight-thumb"></div>
          <div className="map-highlight-text">
            <strong>Colombia</strong>
            <span>Principal operación</span>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function AdminDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    setError("");

    try {
      const token = localStorage.getItem("token");

      if (!token) {
        throw new Error("No hay sesión activa. Inicia sesión nuevamente.");
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/dashboard`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const responseText = await response.text();

      if (!response.ok) {
        throw new Error(responseText || "No se pudo cargar el dashboard");
      }

      const data = responseText ? JSON.parse(responseText) : null;
      setDashboard(data);
    } catch (err) {
      setError(err.message || "Ocurrió un error al cargar el dashboard");
      setDashboard(null);
    } finally {
      setLoading(false);
    }
  };

  const monthlyActivity = useMemo(() => {
    return dashboard?.monthlyActivity || [];
  }, [dashboard]);

  if (loading) {
    return (
      <div className="admin-page">
        <p>Cargando dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-page">
        <p className="admin-form-message error">{error}</p>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="admin-page">
        <p>No se pudo cargar la información del dashboard.</p>
      </div>
    );
  }

  return (
    <div className="admin-page">
      <div className="dashboard-top-grid">
        <MainHeroCard
          activeUsers={dashboard.activeUsers || 0}
          todayRegistrations={dashboard.todayRegistrations || 0}
          monthRegistrations={dashboard.monthRegistrations || 0}
        />

        <SideScoreCard
          total={dashboard.totalPets || 0}
          dogs={dashboard.dogs || 0}
          cats={dashboard.cats || 0}
        />
      </div>

      <div className="dashboard-bottom-grid dashboard-bottom-grid-custom">
        <MonthlyActivityCard data={monthlyActivity} />
        <AlliesSummaryCard
          totalAllies={dashboard.totalAllies || 0}
          recentAllies={dashboard.recentAllies || []}
        />
        <DistributionMapCard />
      </div>
    </div>
  );
}